import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { DropdownInput, useFormContext } from "@validereinc/common-components";
import { FormCategoryAdapter } from "@validereinc/domain";
import { debounce } from "es-toolkit/compat";
import React, { useMemo, useState } from "react";

export const FormCategoryDropdownInput = ({
  filter,
  name,
  ...restProps
}: any) => {
  const { watch } = useFormContext();
  const [searchTerm, setSearchTerm] = useState("");
  const value = watch(name);
  const valueOption = useQuery({
    queryKey: ["formCategory", { filter, value }] as [
      string,
      { filter: any; value: string },
    ],
    queryFn: async ({ queryKey: [_entity, { filter, value }] }) => {
      if (!value) {
        return null;
      }

      const { data } = await FormCategoryAdapter.getList({
        page: 1,
        pageSize: 1,
        filters: {
          ...filter,
          id: value,
        },
      });

      return data?.[0] || null;
    },
  });

  const query = useInfiniteQuery({
    queryKey: ["formCategory", filter, searchTerm],
    queryFn: async ({ queryKey: [_entity, filters, searchTerm], pageParam }) =>
      await FormCategoryAdapter.getList({
        page: pageParam ?? 1,
        sortBy: "name",
        sortDirection: "asc",
        pageSize: 25,
        filters: {
          ...filters,
          ...(searchTerm ? { name: { $like: searchTerm } } : {}),
        },
      }),
    getNextPageParam: (lastPage) => lastPage?.page_number + 1,
    getPreviousPageParam: (prevPage) => prevPage?.page_number - 1,
    enabled: true,
    staleTime: 1000 * 60 * 5,
  });

  const onSearchTermChange = debounce((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
    query.refetch();
  }, 500);

  const onScroll = ({ isBottom }: any) => {
    if (isBottom) {
      query.fetchNextPage();
    }
  };

  const options = useMemo(() => {
    const selected = valueOption.data;

    const options =
      query.data?.pages?.flatMap((page) =>
        page.data.filter(({ id }) => id !== selected?.id)
      ) ?? [];

    return [...(selected ? [selected] : []), ...options];
  }, [valueOption.data, query.data]);

  return (
    <DropdownInput
      name={name}
      options={options}
      valueKey="id"
      labelKey="name"
      searchTerm={searchTerm}
      onSearchTermChange={onSearchTermChange}
      onScroll={onScroll}
      isMulti={false}
      isDisabled={query?.isLoading}
      isFluid
      defaultValue={value ?? ""}
      {...restProps}
    />
  );
};
